import React, { useEffect, useRef, useState } from 'react';
import './ServerStatusToast.css';
import useServerStatus from '../hooks/useServerStatus';

const EXIT_ANIMATION_DURATION = 320;
const ONLINE_VISIBLE_DURATION = 10000;

const ServerStatusToast = () => {
  const status = useServerStatus();
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const prevStatus = useRef();
  const hideTimeoutRef = useRef();
  const exitTimeoutRef = useRef();

  useEffect(() => {
    clearTimeout(hideTimeoutRef.current);

    if (status === 'offline') {
      setShouldRender(true);
      setIsVisible(true);
    } else if (status === 'online') {
      if (prevStatus.current !== 'online') {
        setShouldRender(true);
        setIsVisible(true);
      }
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, ONLINE_VISIBLE_DURATION);
    } else if (status === 'checking') {
      setShouldRender(true);
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    prevStatus.current = status;

    return () => {
      clearTimeout(hideTimeoutRef.current);
    };
  }, [status]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    if (isVisible) {
      clearTimeout(exitTimeoutRef.current);
      return undefined;
    }

    exitTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
    }, EXIT_ANIMATION_DURATION);

    return () => {
      clearTimeout(exitTimeoutRef.current);
    };
  }, [isVisible, shouldRender]);

  useEffect(() => () => {
    clearTimeout(hideTimeoutRef.current);
    clearTimeout(exitTimeoutRef.current);
  }, []);

  if (!shouldRender) return null;

  const text =
    status === 'online'
      ? 'Server online'
      : status === 'offline'
      ? 'Server offline'
      : 'Checking server...';

  const icon =
    status === 'online' ? '✔️' : status === 'offline' ? '❌' : '⏳';

  const toneClass = status || 'checking';
  const visibilityClass = isVisible ? 'show' : 'hide';

  return (
    <div className={`server-status-toast ${toneClass} ${visibilityClass}`} role="status" aria-live="polite">
      <span className="status-icon" aria-hidden="true">
        {icon}
      </span>
      {text}
    </div>
  );
};

export default ServerStatusToast;
